import { ILocationResponse, NodeTypeCodes } from '@kcftech/prometheus-client';
import { AdvancedSettings } from 'components/node_settings/V4EditSettingsModal/AdvancedSettingsTab/AdvancedSettings';
import { CollectionSettings } from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/CollectionSettings';
import { useV4SettingsValidity } from 'components/node_settings/V4EditSettingsModal/useV4SettingsValidity';
import { V4_SETTINGS_TEXT } from 'components/node_settings/v4SettingsTexts';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useSamplingFrequencyOptions } from 'custom_hooks/data_hooks/nodes/useSamplingFrequencyOptions';
import { useUpdateLocationV4SensorSettings } from 'custom_hooks/data_hooks/v4_sensor_settings/useV4SensorSettings';
import { V4SensorSettings } from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import {
    ActionRow,
    EditSectionTitle,
    SettingsCard,
} from 'pages/Settings/V4SensorSettings/styled';
import { v4VibrationOptions } from 'pages/workbench/side_panel/workbench_tree/AssetCreationWizard/inputs/assign_sensors/useNumberOfSamplesOptions';
import { FormProvider, useForm } from 'react-hook-form';
import { StyledButton } from 'shared/button/Button';
import { ConfirmCloseButton } from 'shared/triggered_popup/ConfirmCloseButton';
import { OptionChild } from 'utils/node';
import { sentryCaptureException } from 'utils/sentry';

type Props = Readonly<{
    location: ILocationResponse;
    settings: V4SensorSettings;
    updateIntervalOptions: OptionChild<number>[];
    onDone: () => void;
}>;

export function V4SensorSettingsEditor({
    location,
    settings,
    updateIntervalOptions,
    onDone,
}: Props) {
    const { showToast } = useNotificationContext();
    const { mutate, isPending: isSaving } = useUpdateLocationV4SensorSettings();

    // Mounted only once the settings have loaded, so defaultValues are the saved preset.
    const form = useForm<V4SensorSettings>({
        defaultValues: settings,
        mode: 'onChange',
    });
    const {
        formState: { isDirty, isValid },
        handleSubmit,
    } = form;

    const { validOptions: samplingFrequencyOptions } = useSamplingFrequencyOptions(
        NodeTypeCodes.KcfVibrationV4,
        undefined,
    );

    const {
        collectionDurationInvalid,
        lowFrequencyCollectionInvalid,
        envelopedDataDurationInvalid,
    } = useV4SettingsValidity(form);

    const onSave = handleSubmit((values) =>
        mutate(
            { locationId: location.Id, settings: values },
            {
                onSuccess: () => {
                    showToast({
                        type: 'success',
                        content: V4_SETTINGS_TEXT.modal.toastSaveSuccess,
                    });
                    onDone();
                },
                onError: (error) => {
                    sentryCaptureException({
                        error,
                        tags: { component: 'V4SensorSettingsEditor' },
                        context: { mutationError: { locationId: location.Id } },
                    });
                    showToast({
                        type: 'error',
                        content: V4_SETTINGS_TEXT.modal.toastSaveError,
                    });
                },
            },
        ),
    );

    const actions = (
        <ActionRow>
            <ConfirmCloseButton
                onClose={onDone}
                shouldConfirm={isDirty && !isSaving}
                confirmMessage={V4_SETTINGS_TEXT.modal.confirmStopEditing}
                confirmLabel={V4_SETTINGS_TEXT.modal.confirmStopEditingLabel}
                appearance="secondary"
                spacing="compact"
                test-id="V4SensorSettingsEditor__ConfirmClosePopup"
            >
                Cancel
            </ConfirmCloseButton>
            <StyledButton
                type="button"
                appearance="primary"
                spacing="compact"
                onClick={() => void onSave()}
                disabled={
                    !isDirty ||
                    !isValid ||
                    isSaving ||
                    collectionDurationInvalid ||
                    lowFrequencyCollectionInvalid ||
                    envelopedDataDurationInvalid
                }
                isLoading={isSaving}
            >
                Save
            </StyledButton>
        </ActionRow>
    );

    return (
        <FormProvider {...form}>
            {actions}
            <SettingsCard>
                <div>
                    <EditSectionTitle>
                        {V4_SETTINGS_TEXT.sections.collectionSettings}
                    </EditSectionTitle>
                    <CollectionSettings
                        description={
                            V4_SETTINGS_TEXT.descriptions
                                .collectionSettingsLocationDescription
                        }
                        samplingFrequencyOptions={samplingFrequencyOptions}
                        numberOfSamplesOptions={v4VibrationOptions}
                        updateIntervalOptions={updateIntervalOptions}
                    />
                </div>
                <div>
                    <EditSectionTitle>
                        {V4_SETTINGS_TEXT.sections.advancedSettings}
                    </EditSectionTitle>
                    <AdvancedSettings />
                </div>
            </SettingsCard>
            {actions}
        </FormProvider>
    );
}
